<template>
    <section :class="[
        'tw-rounded-2xl tw-p-6 tw-shadow-2xl',
        theme === 'dark' ? 'tw-bg-[#1a1d21]' : 'tw-bg-white'
    ]">
        <div class="tw-flex tw-items-center tw-gap-3 tw-mb-4">
            <div class="tw-p-2.5 tw-rounded-xl" :class="theme === 'dark' ? 'tw-bg-indigo-500/10 tw-text-indigo-400' : 'tw-bg-indigo-50 tw-text-indigo-600'">
                <svg class="tw-w-6 tw-h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <div>
                <h3 :class="theme === 'dark' ? 'tw-text-slate-100' : 'tw-text-slate-800'" class="tw-font-bold">Programa de Curvas</h3>
                <p :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-xs tw-font-medium tw-mt-1">Média atual: <span class="tw-font-bold tw-text-blue-500">{{ programaCurva['media'] }}°C</span><span class="tw-mx-2">·</span>Temperatura ambiente: <span class="tw-font-bold tw-text-blue-500">{{ temperaturaAr != null ? temperaturaAr + '°C' : '--' }}</span></p>
            </div>
        </div>

        <div class="tw-grid tw-grid-cols-3 tw-gap-2 tw-mb-4 tw-text-center">
            <div :class="theme === 'dark' ? 'tw-bg-slate-800' : 'tw-bg-gray-100'" class="tw-rounded-xl tw-p-2">
                <p :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-[10px] tw-font-bold tw-uppercase">UR Externo</p>
                <p :class="theme === 'dark' ? 'tw-text-slate-100' : 'tw-text-slate-800'" class="tw-text-sm tw-font-bold">
                    {{ umidadeAr != null ? umidadeAr + '%' : '--' }}
                </p>
            </div>
            <div :class="theme === 'dark' ? 'tw-bg-slate-800' : 'tw-bg-gray-100'" class="tw-rounded-xl tw-p-2">
                <p :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-[10px] tw-font-bold tw-uppercase">Diferença</p>
                <p :class="theme === 'dark' ? 'tw-text-slate-100' : 'tw-text-slate-800'" class="tw-text-sm tw-font-bold">
                    {{ diferenca != null ? diferenca.toFixed(1) + '°C' : '--' }}
                </p>
            </div>
            <div class="tw-rounded-xl tw-p-2" :style="zonaAtual ? { backgroundColor: zonaAtual.cor } : {}" :class="!zonaAtual && (theme === 'dark' ? 'tw-bg-slate-800' : 'tw-bg-gray-100')">
                <p :class="zonaAtual ? 'tw-text-slate-900/70' : (theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500')" class="tw-text-[10px] tw-font-bold tw-uppercase">Curva</p>
                <p :class="zonaAtual ? 'tw-text-slate-900' : (theme === 'dark' ? 'tw-text-slate-100' : 'tw-text-slate-800')" class="tw-text-sm tw-font-bold">
                    {{ zonaAtual ? zonaAtual.id : '--' }}
                </p>
            </div>
        </div>

        <div v-if="diferenca != null && diferenca < 0" :class="theme === 'dark' ? 'tw-bg-sky-500/10 tw-text-sky-300 tw-border-sky-500/30' : 'tw-bg-sky-50 tw-text-sky-700 tw-border-sky-200'" class="tw-flex tw-items-start tw-gap-2 tw-rounded-xl tw-border tw-px-3 tw-py-2 tw-mb-4 tw-text-xs tw-font-medium">
            <svg class="tw-w-4 tw-h-4 tw-shrink-0 tw-mt-px" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            <span>A massa de grãos já está mais fria que o ar externo ({{ Math.abs(diferenca).toFixed(1) }}°C abaixo). Aeração neste momento não resfriaria o produto.</span>
        </div>

        <svg :viewBox="`0 0 ${W} ${H}`" class="tw-w-full tw-h-auto" role="img" aria-label="Gráfico de recomendação de aeração">
            <rect :x="PL" :y="PT" :width="plotW" :height="plotH" :fill="ZONAS[3].cor" />
            <path :d="areaAbaixo(CURVA_3_4)" :fill="ZONAS[2].cor" />
            <path :d="areaAbaixo(CURVA_2_3)" :fill="ZONAS[1].cor" />
            <path :d="areaAbaixo(CURVA_1_2)" :fill="ZONAS[0].cor" />

            <g :stroke="theme === 'dark' ? 'rgba(15,23,42,0.35)' : 'rgba(15,23,42,0.25)'" stroke-width="0.5">
                <line v-for="ur in GRADE_X" :key="'gx' + ur" :x1="sx(ur)" :x2="sx(ur)" :y1="PT" :y2="PT + plotH" />
                <line v-for="dt in GRADE_Y" :key="'gy' + dt" :x1="PL" :x2="PL + plotW" :y1="sy(dt)" :y2="sy(dt)" />
            </g>
            <rect :x="PL" :y="PT" :width="plotW" :height="plotH" fill="none" :stroke="corEixo" stroke-width="1.2" />

            <g font-weight="800" font-size="14" fill="#fff" stroke="#0f172a" stroke-width="3.5" stroke-linejoin="round" paint-order="stroke" text-anchor="middle">
                <text v-for="rotulo in rotulosZonas" :key="rotulo.id" :x="rotulo.x" :y="rotulo.y">
                    <tspan v-for="(linha, i) in rotulo.linhas" :key="i" :x="rotulo.x" :dy="i === 0 ? 0 : ALTURA_LINHA">{{ linha }}</tspan>
                </text>
            </g>

            <g :fill="corTextoEixo" font-size="10" font-weight="600">
                <text v-for="ur in GRADE_X" :key="'lx' + ur" :x="sx(ur)" :y="PT + plotH + 14" text-anchor="middle">{{ ur }}</text>
                <text v-for="dt in ROTULOS_Y" :key="'ly' + dt" :x="PL - 6" :y="sy(dt) + 3" text-anchor="end">{{ dt }}</text>
            </g>
            <g fill="#fff" font-size="10" font-weight="700" text-anchor="middle">
                <rect :x="0" :y="PT" :width="BARRA" :height="plotH" :fill="AZUL_LEGENDA" />
                <text :transform="`translate(${BARRA / 2 + 3.5} ${PT + plotH / 2}) rotate(-90)`">DIFERENÇA DE TEMPERATURA GRÃOS × AR AMBIENTE (°C)</text>
                <rect :x="PL" :y="H - BARRA" :width="plotW" :height="BARRA" :fill="AZUL_LEGENDA" />
                <text :x="PL + plotW / 2" :y="H - BARRA / 2 + 3.5">UMIDADE RELATIVA DO AR AMBIENTE (EXTERNO) U.R. %</text>
            </g>

            <g v-if="ponto">
                <line :x1="ponto.x" :x2="ponto.x" :y1="ponto.y" :y2="PT + plotH" stroke="#0f172a" stroke-width="1" stroke-dasharray="3 3" />
                <line :x1="PL" :x2="ponto.x" :y1="ponto.y" :y2="ponto.y" stroke="#0f172a" stroke-width="1" stroke-dasharray="3 3" />
                <circle :cx="ponto.x" :cy="ponto.y" r="9" fill="#2563eb" opacity="0.25">
                    <animate attributeName="r" values="7;12;7" dur="2s" repeatCount="indefinite" />
                </circle>
                <circle :cx="ponto.x" :cy="ponto.y" r="5.5" fill="#2563eb" stroke="#fff" stroke-width="2" />
            </g>
        </svg>

        <p v-if="!ponto" :class="theme === 'dark' ? 'tw-text-slate-400' : 'tw-text-slate-500'" class="tw-text-xs tw-text-center tw-mt-2">
            {{ carregandoEstacao ? 'Carregando dados da estação...' : 'Sem dados da estação ou média dos grãos para posicionar o ponto atual.' }}
        </p>
    </section>
</template>
<script>
const CURVA_1_2 = [[50, 0], [55, 0.75], [60, 1.45], [65, 2.0], [70, 2.5], [75, 3.0], [80, 3.45], [85, 3.9], [90, 4.3], [95, 4.65], [100, 5.0]];
const CURVA_2_3 = [[35, 0], [40, 0.9], [45, 1.55], [50, 2.2], [55, 2.75], [60, 3.3], [65, 3.9], [70, 4.4], [75, 4.85], [80, 5.3], [85, 5.7], [90, 6.1], [95, 6.5], [100, 6.85]];
const CURVA_3_4 = [[30, 1.45], [35, 2.2], [40, 3.0], [45, 3.65], [50, 4.2], [55, 4.75], [60, 5.2], [65, 5.7], [70, 6.2], [75, 6.65], [80, 7.1], [85, 7.5], [90, 7.85], [95, 8.2], [100, 8.55]];

const UR_MIN = 30, UR_MAX = 100, DT_MIN = 0, DT_MAX = 9.5;

const valorCurva = (curva, ur) => {
    if (ur <= curva[0][0]) return curva[0][1];
    for (let i = 1; i < curva.length; i++) {
        const [x0, y0] = curva[i - 1];
        const [x1, y1] = curva[i];
        if (ur <= x1) return y0 + (y1 - y0) * (ur - x0) / (x1 - x0);
    }
    return curva[curva.length - 1][1];
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const ZONAS = [
    { id: 1, nome: 'Aeração sem interesse', cor: '#fde047', linhas: ['1 · AERAÇÃO', 'SEM INTERESSE'],
        limites: (ur) => [DT_MIN, valorCurva(CURVA_1_2, ur)] },
    { id: 2, nome: 'Aeração possível', cor: '#fb923c', linhas: ['2 · AERAÇÃO', 'POSSÍVEL'],
        limites: (ur) => [valorCurva(CURVA_1_2, ur), valorCurva(CURVA_2_3, ur)] },
    { id: 3, nome: 'Aeração recomendável', cor: '#4ade80', linhas: ['3 · AERAÇÃO', 'RECOMENDÁVEL'],
        limites: (ur) => [valorCurva(CURVA_2_3, ur), valorCurva(CURVA_3_4, ur)] },
    { id: 4, nome: 'Aeração recomendável mas com risco de secagem', cor: '#d1d5db', linhas: ['4 · AERAÇÃO RECOMENDÁVEL', 'MAS COM RISCO DE SECAGEM'],
        limites: (ur) => [valorCurva(CURVA_3_4, ur), DT_MAX] },
];

const centroZona = (zona) => {
    let soma = 0, somaX = 0;
    for (let ur = UR_MIN; ur <= UR_MAX; ur += 0.5) {
        const [inf, sup] = zona.limites(ur);
        const altura = Math.max(0, sup - inf);
        soma += altura;
        somaX += ur * altura;
    }
    const ur = somaX / soma;
    const [inf, sup] = zona.limites(ur);
    return { ur, dt: (inf + sup) / 2 };
};

const toNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(String(v).replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};

export default {
    props: {
        parametros: {
            type: Object,
            default: () => ({})
        },
        theme: {
            type: String,
            default: 'light'
        }
    },
    data() {
        return {
            W: 560,
            H: 580,
            PL: 48,
            PR: 8,
            PT: 8,
            PB: 44,
            BARRA: 22,
            AZUL_LEGENDA: '#1e40af',
            CURVA_1_2,
            CURVA_2_3,
            CURVA_3_4,
            ZONAS,
            GRADE_X: [30, 40, 50, 60, 70, 80, 90, 100],
            GRADE_Y: [1, 2, 3, 4, 5, 6, 7, 8, 9],
            ROTULOS_Y: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
            ALTURA_LINHA: 16,
            estacao: null,
            carregandoEstacao: true,
        };
    },
    async mounted() {
        try {
            const response = await this.$store.dispatch('unidade/getEstacaoByUnidade', this.$route.params.unidade);
            this.estacao = response?.estacao || null;
        } catch (error) {
            this.estacao = null;
        }
        this.carregandoEstacao = false;
    },
    computed: {
        programaCurva() {
            if (this.parametros && this.parametros['4']) {
                return this.parametros['4']
            }
            return {
                "media": ""
            }
        },
        plotW() {
            return this.W - this.PL - this.PR;
        },
        plotH() {
            return this.H - this.PT - this.PB;
        },
        corEixo() {
            return this.theme === 'dark' ? '#94a3b8' : '#334155';
        },
        corTextoEixo() {
            return this.theme === 'dark' ? '#cbd5e1' : '#334155';
        },
        temperaturaGraos() {
            return toNumber(this.programaCurva['media']);
        },
        temperaturaAr() {
            return toNumber(this.estacao?.temperatura);
        },
        umidadeAr() {
            return toNumber(this.estacao?.umidade);
        },
        diferenca() {
            if (this.temperaturaGraos == null || this.temperaturaAr == null) return null;
            return this.temperaturaGraos - this.temperaturaAr;
        },
        zonaAtual() {
            if (this.diferenca == null || this.umidadeAr == null) return null;
            const ur = clamp(this.umidadeAr, UR_MIN, UR_MAX);
            const dt = this.diferenca;
            if (dt < valorCurva(CURVA_1_2, ur)) return ZONAS[0];
            if (dt < valorCurva(CURVA_2_3, ur)) return ZONAS[1];
            if (dt < valorCurva(CURVA_3_4, ur)) return ZONAS[2];
            return ZONAS[3];
        },
        rotulosZonas() {
            return ZONAS.map((zona) => {
                const { ur, dt } = centroZona(zona);
                const alturaBloco = 10 + (zona.linhas.length - 1) * this.ALTURA_LINHA;
                return {
                    id: zona.id,
                    linhas: zona.linhas,
                    x: this.sx(ur),
                    y: this.sy(dt) - alturaBloco / 2 + 10,
                };
            });
        },
        ponto() {
            if (this.diferenca == null || this.umidadeAr == null) return null;
            return {
                x: this.sx(clamp(this.umidadeAr, UR_MIN, UR_MAX)),
                y: this.sy(clamp(this.diferenca, DT_MIN, DT_MAX)),
            };
        },
    },
    methods: {
        sx(ur) {
            return this.PL + (ur - UR_MIN) / (UR_MAX - UR_MIN) * this.plotW;
        },
        sy(dt) {
            return this.PT + this.plotH - (dt - DT_MIN) / (DT_MAX - DT_MIN) * this.plotH;
        },
        areaAbaixo(curva) {
            const base = this.sy(DT_MIN);
            const pontos = curva.map(([ur, dt]) => `${this.sx(ur)},${this.sy(dt)}`);
            const xIni = this.sx(curva[0][0]);
            const xFim = this.sx(curva[curva.length - 1][0]);
            return `M${xIni},${base} L${pontos.join(' L')} L${xFim},${base} Z`;
        },
    }
}
</script>